import { FileContainer, process } from "am-tax-fe-core";

import { components } from "../generated/generated-api";
import { uploadFile, uploadForm } from "../uploadUtils";
import { cachedApiUrl, client } from "../entities/entityApi";

//#region Dto definition
export type Transaction = components["schemas"]["TransactionDto"];
export type CreateTransaction = {
    /** Format: uuid */
    clientId: string;
    name: string;
    category: components["schemas"]["Category"];
    type: components["schemas"]["TransactionType"];
    otherTransactionType?: string;
    serviceProviders: string[];
    serviceRecipients: string[];
    method: components["schemas"]["Method"];
    testedParties: string[];
    benchmarkAnalysisBlobPath: string;
    benchmarkDataJsonBlobPath?: string;
    /** Format: int32 */
    year?: number;
    researchAndDevelopment?: string;
    purchasing?: string;
    manufacturingAndAssembly?: string;
    qualityAssurance?: string;
    marketingAndSales?: string;
    distributionAndLogistics?: string;
    afterSalesServices?: string;
    managementAndAdministration?: string;
    otherFunction?: string;
    manufacturingAndProduction?: string;
    inventory?: string;
    productLiabilityAndWarranty?: string;
    market?: string;
    foreignExchange?: string;
    creditAndCollection?: string;
    intangiblePropertyInfringement?: string;
    otherRisk?: string;
    assetsTangible?: string;
    assetsTrademarksAndTradenames?: string;
    assetsTechnicalKnowHow?: string;
    assetsPatents?: string;
    assetsOther?: string;
    File?: string;
};
export type UpdateTransaction = {
    /** Format: uuid */
    id: string;
    /** Format: uuid */
    clientId: string;
    name: string;
    category: components["schemas"]["Category"];
    type: components["schemas"]["TransactionType"];
    otherTransactionType?: string;
    serviceProviders: string[];
    serviceRecipients: string[];
    method: components["schemas"]["Method"];
    testedParties: string[];
    benchmarkAnalysisBlobPath: string;
    benchmarkDataJsonBlobPath?: string;
    /** Format: int32 */
    year?: number;
    researchAndDevelopment?: string;
    purchasing?: string;
    manufacturingAndAssembly?: string;
    qualityAssurance?: string;
    marketingAndSales?: string;
    distributionAndLogistics?: string;
    afterSalesServices?: string;
    managementAndAdministration?: string;
    otherFunction?: string;
    manufacturingAndProduction?: string;
    inventory?: string;
    productLiabilityAndWarranty?: string;
    market?: string;
    foreignExchange?: string;
    creditAndCollection?: string;
    intangiblePropertyInfringement?: string;
    otherRisk?: string;
    assetsTangible?: string;
    assetsTrademarksAndTradenames?: string;
    assetsTechnicalKnowHow?: string;
    assetsPatents?: string;
    assetsOther?: string;
    File?: string;
};

export const getTransactionAsync = async (id: string): Promise<Transaction> =>
    process(
        client().GET("/api/transactions/{transactionId}", {
            params: {
                path: {
                    transactionId: id,
                },
            },
        }),
    );

export const getTransactionsAsync = async (): Promise<Transaction[]> => process(client().GET("/api/transactions"));

export const getTransactionsForUserAsync = async (): Promise<Transaction[]> => process(client().GET("/api/transactions/user"));

export const getTransactionsByClientIdAsync = async (clientId: string): Promise<Transaction[]> =>
    process(
        client().GET("/api/transactions/client/{clientId}", {
            params: {
                path: {
                    clientId: clientId,
                },
            },
        }),
    );

export const updateTransactionAsync = async (transaction: UpdateTransaction, file: FileContainer | undefined) => {
    // Map camelCase properties to PascalCase for the API
    const transactionData: Record<string, string> = {
        Id: transaction.id,
        ClientId: transaction.clientId,
        Name: transaction.name,
        Category: transaction.category,
        Type: transaction.type,
        OtherTransactionType: transaction.otherTransactionType ?? "",
        ServiceProviders: transaction.serviceProviders.join(","),
        ServiceRecipients: transaction.serviceRecipients.join(","),
        Method: transaction.method,
        TestedParties: transaction.testedParties.join(","),
        BenchmarkAnalysisBlobPath: transaction.benchmarkAnalysisBlobPath,
        BenchmarkDataJsonBlobPath: transaction.benchmarkDataJsonBlobPath ?? "",
        Year: transaction.year?.toString() ?? "",
        researchAndDevelopment: transaction?.researchAndDevelopment ?? "",
        purchasing: transaction?.purchasing ?? "",
        manufacturingAndAssembly: transaction?.manufacturingAndAssembly ?? "",
        qualityAssurance: transaction?.qualityAssurance ?? "",
        marketingAndSales: transaction?.marketingAndSales ?? "",
        distributionAndLogistics: transaction?.distributionAndLogistics ?? "",
        afterSalesServices: transaction?.afterSalesServices ?? "",
        managementAndAdministration: transaction?.managementAndAdministration ?? "",
        otherFunction: transaction?.otherFunction ?? "",
        manufacturingAndProduction: transaction?.manufacturingAndProduction ?? "",
        inventory: transaction?.inventory ?? "",
        productLiabilityAndWarranty: transaction?.productLiabilityAndWarranty ?? "",
        market: transaction?.market ?? "",
        foreignExchange: transaction?.foreignExchange ?? "",
        creditAndCollection: transaction?.creditAndCollection ?? "",
        intangiblePropertyInfringement: transaction?.intangiblePropertyInfringement ?? "",
        otherRisk: transaction?.otherRisk ?? "",
        assetsTangible: transaction?.assetsTangible ?? "",
        assetsTrademarksAndTradenames: transaction?.assetsTrademarksAndTradenames ?? "",
        assetsTechnicalKnowHow: transaction?.assetsTechnicalKnowHow ?? "",
        assetsPatents: transaction?.assetsPatents ?? "",
        assetsOther: transaction?.assetsOther ?? "",
    };

    if (file !== undefined) {
        await uploadFile(`${cachedApiUrl}/api/transactions/${transaction.id}`, file, transactionData, "PUT");
    } else {
        await uploadForm(`${cachedApiUrl}/api/transactions/${transaction.id}`, transactionData);
    }
};

export const deleteTransactionAsync = async (id: string): Promise<Transaction> =>
    process(
        client().DELETE("/api/transactions/{transactionId}", {
            params: {
                path: {
                    transactionId: id,
                },
            },
        }),
    );

export const uploadBenchmarkFileAsync = async (file: FileContainer, transactionId: string) => {
    return uploadFile(`${cachedApiUrl}/api/transactions/${transactionId}/benchmark`, file);
};

export const createTransactionWithBenchmarkAnalysisAsync = async (transaction: CreateTransaction, file: FileContainer) => {
    // Map camelCase properties to PascalCase for the API
    const transactionData: Record<string, string> = {
        ClientId: transaction.clientId,
        Name: transaction.name,
        Category: transaction.category,
        Type: transaction.type,
        OtherTransactionType: transaction.otherTransactionType ?? "",
        ServiceProviders: transaction.serviceProviders.join(","),
        ServiceRecipients: transaction.serviceRecipients.join(","),
        Method: transaction.method,
        TestedParties: transaction.testedParties.join(","),
        BenchmarkAnalysisBlobPath: transaction.benchmarkAnalysisBlobPath,
        BenchmarkDataJsonBlobPath: transaction.benchmarkDataJsonBlobPath ?? "",
        Year: transaction.year?.toString() ?? "",
        researchAndDevelopment: transaction?.researchAndDevelopment ?? "",
        purchasing: transaction?.purchasing ?? "",
        manufacturingAndAssembly: transaction?.manufacturingAndAssembly ?? "",
        qualityAssurance: transaction?.qualityAssurance ?? "",
        marketingAndSales: transaction?.marketingAndSales ?? "",
        distributionAndLogistics: transaction?.distributionAndLogistics ?? "",
        afterSalesServices: transaction?.afterSalesServices ?? "",
        managementAndAdministration: transaction?.managementAndAdministration ?? "",
        otherFunction: transaction?.otherFunction ?? "",
        manufacturingAndProduction: transaction?.manufacturingAndProduction ?? "",
        inventory: transaction?.inventory ?? "",
        productLiabilityAndWarranty: transaction?.productLiabilityAndWarranty ?? "",
        market: transaction?.market ?? "",
        foreignExchange: transaction?.foreignExchange ?? "",
        creditAndCollection: transaction?.creditAndCollection ?? "",
        intangiblePropertyInfringement: transaction?.intangiblePropertyInfringement ?? "",
        otherRisk: transaction?.otherRisk ?? "",
        assetsTangible: transaction?.assetsTangible ?? "",
        assetsTrademarksAndTradenames: transaction?.assetsTrademarksAndTradenames ?? "",
        assetsTechnicalKnowHow: transaction?.assetsTechnicalKnowHow ?? "",
        assetsPatents: transaction?.assetsPatents ?? "",
        assetsOther: transaction?.assetsOther ?? "",
        File: file.file.name, // Add the File property that the API expects
    };
    await uploadFile(`${cachedApiUrl}/api/transactions`, file, transactionData);
};

export const downloadBenchmarkAnalysisAsync = async (transactionId: string): Promise<Response> => {
    const result = await client().GET("/api/transactions/{transactionId}/benchmark/download", {
        params: {
            path: {
                transactionId: transactionId,
            },
        },
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download benchmark analysis: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};

export const downloadBenchmarkTemplateAsync = async (): Promise<Response> => {
    const result = await client().GET("/api/transactions/benchmark-template/download", {
        parseAs: "stream",
    });

    if (!result.response || !result.response.ok) {
        throw new Error(`Failed to download template: ${result.response?.statusText || "Unknown error"}`);
    }

    return result.response;
};
